// Detalle del proyecto: timeline de milestones + acciones
// "Marcar completado" y "Facturar progreso".

import { useState } from 'react';

import {
  useCompleteMilestone,
  useInvoiceNow,
  useProjectDetail,
} from '../hooks/useProjects';
import type { MilestoneOut, MilestoneStatus } from '../types';

const STATUS_LABEL: Record<MilestoneStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  done: 'Completado',
  blocked: 'Bloqueado',
};

interface Props {
  projectId: string | null;
}

export function ProjectDetail({ projectId }: Props) {
  const { data, loading, error, refetch } = useProjectDetail(projectId);
  const { complete, loading: completing } = useCompleteMilestone();
  const { request: invoiceNow, loading: invoicing } = useInvoiceNow();
  const [lastInvoice, setLastInvoice] = useState<string | null>(null);

  if (!projectId) {
    return (
      <div className="text-sm text-neutral-500">
        Selecciona un proyecto en la lista para ver su detalle.
      </div>
    );
  }
  if (loading && !data) {
    return <div className="text-sm text-neutral-500">Cargando proyecto…</div>;
  }
  if (error) {
    return <div className="text-sm text-red-600">Error: {error}</div>;
  }
  if (!data) return null;

  const onComplete = async (m: MilestoneOut) => {
    if (m.status === 'done') return;
    await complete(data.id, m.id);
    await refetch();
  };

  const onInvoice = async () => {
    const result = await invoiceNow(data.id);
    if (result && typeof result === 'object' && 'invoice_ref' in result) {
      setLastInvoice((result as { invoice_ref: string }).invoice_ref);
    }
    await refetch();
  };

  const billable = data.milestones.filter(
    (m) => m.status === 'done' && !m.invoiced_at && m.amount_due,
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{data.name}</h2>
        {data.description && (
          <p className="text-sm text-neutral-600 mt-1">{data.description}</p>
        )}
        <div className="text-xs text-neutral-500 mt-2">
          Status: <span className="font-medium">{data.status}</span> ·{' '}
          Completado: {data.amount_completed} {data.currency} ·{' '}
          Facturado: {data.amount_billed} {data.currency}
          {data.total_budget && (
            <>
              {' '}· Presupuesto: {data.total_budget} {data.currency}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onInvoice}
          disabled={invoicing || billable.length === 0}
          className="bg-blue-600 text-white text-sm rounded px-3 py-1 disabled:opacity-40"
          title={
            billable.length === 0
              ? 'No hay milestones completados sin facturar'
              : `Facturará ${billable.length} milestone(s)`
          }
        >
          {invoicing ? 'Generando…' : `Facturar progreso (${billable.length})`}
        </button>
        {lastInvoice && (
          <span className="text-xs text-green-700">
            Invoice generada: {lastInvoice}
          </span>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Timeline de milestones</h3>
        <ol className="space-y-2">
          {data.milestones.map((m) => {
            const isDone = m.status === 'done';
            const isInvoiced = !!m.invoiced_at;
            return (
              <li
                key={m.id}
                className="border rounded p-3 flex items-start gap-3 bg-white"
              >
                <div
                  className={`mt-1 w-3 h-3 rounded-full ${
                    isInvoiced
                      ? 'bg-green-500'
                      : isDone
                        ? 'bg-blue-500'
                        : m.status === 'blocked'
                          ? 'bg-red-400'
                          : 'bg-neutral-300'
                  }`}
                  aria-hidden
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{m.title}</div>
                    <div className="text-xs text-neutral-500">
                      {STATUS_LABEL[m.status]}
                      {m.amount_due && ` · ${m.amount_due} ${data.currency}`}
                    </div>
                  </div>
                  {m.description && (
                    <p className="text-xs text-neutral-600 mt-1">
                      {m.description}
                    </p>
                  )}
                  <div className="mt-2 text-xs text-neutral-500 flex items-center gap-2">
                    {m.due_date && <span>Vence: {m.due_date}</span>}
                    {m.completed_at && (
                      <span>· Completado: {new Date(m.completed_at).toLocaleDateString()}</span>
                    )}
                    {isInvoiced && (
                      <span className="text-green-700">
                        · Invoice: {m.invoice_ref}
                      </span>
                    )}
                  </div>
                  {!isDone && (
                    <div className="mt-2">
                      <button
                        onClick={() => onComplete(m)}
                        disabled={completing}
                        className="text-xs border rounded px-2 py-1 hover:bg-neutral-50 disabled:opacity-40"
                      >
                        Marcar completado
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
